import { FormikProps, withFormik } from 'formik';
import * as React from 'react';
import { AnalyticsExtensionsSettingsStyles } from '../styles/AnalyticsExtensionsSettingsStyles';
import { FeatureFlags } from '../../ts/FeatureFlags';

import {
  ButtonType,
  ButtonWidget,
  CheckBoxWithLabelWidget,
  DialogType,
  DropdownButtonType,
  List,
  ListItem,
  SingleSelectDropdown,
  TextFieldWidget,
  DialogPositioningBehavior,
  TextLinkWidget
} from '@tableau/widgets';
import { Messages } from '../../codegen/Localize';
import { IResult } from '../../ts/ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../../ts/services/ErrorNotifyingServer';
import {
  AnalyticsExtensionsSettingsCtrlProps,
  IAnalyticsExtensionsSettings
} from '../controllers/SiteSettingsForAnalyticsExtensionsCtrl';

import { ModalInstance, ReactModalService } from '../../ts/services/ReactModalService';
import { AnalyticsExtensionsSettingsDialog } from '../components/AnalyticsExtensionsSettingsDialog';
import {
  IAnalyticsExtConnectionId,
  IAnalyticsExtConnectionSettings,
  IAnalyticsExtSiteSettings
} from '../types/ISiteSettingsForSiteAdmin';
import { openConfirmActionDialog } from '../../ts/services/ReactConfirmActionDialog';
import {Simulate} from 'react-dom/test-utils';
import error = Simulate.error;


export interface AnalyticsExtensionsSettingsForSiteValues {
  showAnalyticsSiteSetting: boolean;
  showSiteCheckbox: boolean;
  enableExternalServicesCheckbox: boolean;
  connectionLUID: string;
  connectionName: string;
  connectionType: string;
  hostName: string;
  port?: number;
  isAuth?: boolean;
  userName?: string;
  password?: string;
  isSecure?: boolean;
}

const ConnectionTypes = {
  TABPY: {
    TAG: 'TABPY',
    DISPLAY: 'TabPy/Analytics Extension'
  },
  RSERVE: {
    TAG: 'RSERVE',
    DISPLAY: 'Rserve'
  },
  SELECT: 'Select'
};


const connectionItems: ListItem[] = [
  {value: ConnectionTypes.SELECT},
  {value: ConnectionTypes.TABPY.DISPLAY},
  {value: ConnectionTypes.RSERVE.DISPLAY}
];

const MAX_HOSTNAME_LENGTH: number = 255;
const MIN_PORT_NUMBER: number = 0;
const MAX_PORT_NUMBER: number = 65535;
const MAX_USERNAME_LENGTH: number = 255;
const MAX_PASSWORD_LENGTH: number = 255;

export type InnerAnalyticsExtensionsSettingForSiteProps =
  AnalyticsExtensionsSettingsCtrlProps & FormikProps<AnalyticsExtensionsSettingsForSiteValues> & AnalyticsExtensionsSettingsForSiteValues;

interface AnalyticsExtensionsSettingsForSiteState{
  isBusy: boolean;
  saveFormState: IResult;
}

interface AnExtSiteSettingRemoveActionProps {
  readonly removeConnectionHeader: string;
  readonly removeConnectionText: string;
  readonly connectionLUID: string;
  sendDeleteRequest: (connectionId: IAnalyticsExtConnectionId) => ng.IPromise<IResult>;
}

export const AnExtSiteSettingRemoveActionStringBody = (props: AnExtSiteSettingRemoveActionProps): JSX.Element => {
  return (
    <div>
        <h3 className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogHeader}>
            { Messages.analyticsExtensionsRemoveConnectionHeader() }
        </h3>
        <div className={AnalyticsExtensionsSettingsStyles.connectionDeleteConfirmationDialogDescription}>
          { Messages.analyticsExtensionsRemoveConnectionText() }
        </div>
    </div>
  );
};

export function confirmDeleteConnectionDialog(luid: string, request: (connectionId: IAnalyticsExtConnectionId) => ng.IPromise<IResult>) {
  const dialogInstance = openConfirmActionDialog<AnExtSiteSettingRemoveActionProps, any, IResult>({
    title: null,
    bodyComponent: AnExtSiteSettingRemoveActionStringBody,
    initialBodyProps: {
      removeConnectionHeader: Messages.analyticsExtensionsRemoveConnectionHeader(),
      removeConnectionText: Messages.analyticsExtensionsRemoveConnectionText(),
      connectionLUID: luid,
      sendDeleteRequest: request
    },
    initialDialogState: {},
    actionName: Messages.analyticsExtensionsRemoveConnectionActionName(),
    action: ()=> request({connection_luid: luid}),
    isActionDelete: true
  });
  return dialogInstance.result;
}

export class InnerAnalyticsExtensionsSettingForSite extends React.PureComponent <InnerAnalyticsExtensionsSettingForSiteProps, AnalyticsExtensionsSettingsForSiteState> {
  constructor(props: InnerAnalyticsExtensionsSettingForSiteProps) {
    super(props);
    props.setSaveCallback(() => this.sendUpdateSave());

    if(FeatureFlags.anExtRedesignV20203Enabled) {
      props.setCancelCallback(()=> {
        this.handleChangeAdapter(this.props.initialValues.enableExternalServicesCheckbox, 'enableExternalServicesCheckbox');
        this.props.setIsFormDirty(false);
      });
    } else {
      props.setCancelCallback(props.handleReset);
    }

    props.setIsBusyCallback(() => props.isSubmitting);
    if (FeatureFlags.anExtRedesignV20203Enabled) {
      this.sendDelete = this.sendDelete.bind(this);
    }
  }

  public sendUpdateSave(): ng.IPromise<IResult> {
    // need showAnalyticsSiteSetting check because current design require the hack to handle signle/multisite
    // this is how extension team does it, refactoring requires lots of work and would need to refactor their page
    if (this.props.values.showAnalyticsSiteSetting) {
      const updateSiteSettings: IAnalyticsExtSiteSettings = {enabled: this.props.values.enableExternalServicesCheckbox};
      if(FeatureFlags.anExtRedesignV20203Enabled) {
        this.props.handleSubmit();
        return this.props.sendUpdateRequest(updateSiteSettings, null);
      }

      let connection = '';
      if (this.props.values.connectionType === ConnectionTypes.TABPY.DISPLAY) {
        connection = ConnectionTypes.TABPY.TAG;
      } else if (this.props.values.connectionType === ConnectionTypes.RSERVE.DISPLAY) {
        connection = ConnectionTypes.RSERVE.TAG;
      } else { // No connection created. Only save the site enable/disbale setting
        this.props.handleSubmit();
        return this.props.sendUpdateRequest(updateSiteSettings, null);
      }
      const updateConnectionSettings: IAnalyticsExtConnectionSettings = {
        connection_luid: this.props.values.connectionLUID,
        //connectionName: 'default',
        connection_type: connection,
        host: this.props.values.hostName,
        port: this.props.values.port,
        is_ssl_enabled: this.props.values.isSecure,
        is_auth_enabled: this.props.values.isAuth,
        username: this.props.values.userName,
        password: this.props.values.password

      };
      this.props.handleSubmit();
      return this.props.sendUpdateRequest(updateSiteSettings, updateConnectionSettings);
    }
    this.props.handleSubmit();
  }

  public componentDidUpdate() {
    // Let the Angular controller know things have changed
    this.props.setIsFormDirty(this.isDirty());
    this.props.setIsFormValid(this.props.isValid);
    this.props.setIsBusyCallback(() => this.props.isSubmitting);
  }

  private handleChangeAdapter = (value: any, fieldName: keyof AnalyticsExtensionsSettingsForSiteValues) => {
    const event = {
      target: {
        name: fieldName,
        value
      }
    };
    // Call the Formik handleChange
    this.props.handleChange(event);
  }

  private clearLoginInfo() {
    this.handleChangeAdapter('', 'userName');
    this.handleChangeAdapter('', 'password');
  }

  private showConnectionDialog(ctrlProps: AnalyticsExtensionsSettingsCtrlProps) {
    let modalInstance: ModalInstance;
    const dialogProps = {
      dismiss: () => modalInstance && modalInstance.dismiss(),
      close: (result: any) => modalInstance && modalInstance.close(result),
      ctrlProps: ctrlProps
    };

    modalInstance = ReactModalService.open({
      type: DialogType.Uber,
      dialogContent: React.createElement(AnalyticsExtensionsSettingsDialog, dialogProps),
      testId: 'analyticsConnectionsSettingDialog',
      showCloseButton: true,
      //dialogStyle: serverAndSiteUsersDialogStyle,
      shouldNotSetDefaultWidth: true
    });
    return modalInstance.result;
  }

  private updateConnectionInfo(result: IAnalyticsExtConnectionSettings) {
    if (result) {
      const updateConnection = result;
      let connectionDisplay = '';
      if (updateConnection.connection_type === ConnectionTypes.TABPY.TAG) {
        connectionDisplay = ConnectionTypes.TABPY.DISPLAY;
      }
      if (updateConnection.connection_type === ConnectionTypes.RSERVE.TAG) {
        connectionDisplay = ConnectionTypes.RSERVE.DISPLAY;
      }
      this.handleChangeAdapter(connectionDisplay, 'connectionType');
      this.handleChangeAdapter(updateConnection.connection_luid, 'connectionLUID');
      this.handleChangeAdapter(updateConnection.host, 'hostName');
      this.props.setIsFormDirty(this.isDirty());
    }
  }

  private sendDelete() {
    let result = confirmDeleteConnectionDialog(this.props.values.connectionLUID, this.props.sendDeleteRequest);
    result.then((result: IAnalyticsExtensionsSettings[]) => {
      if (!result.length) {
        this.props.initialAnalyticsExtensionsSettings.connectionDefault = null;
        this.handleChangeAdapter(ConnectionTypes.SELECT, 'connectionType');
      } else {
        this.setState(error);
      }
      this.props.setIsFormDirty(this.isDirty());
    });
  }

  private isDirty() {
    let dirty = this.props.dirty;
    if(FeatureFlags.anExtRedesignV20203Enabled) {
      dirty = this.props.values.enableExternalServicesCheckbox !== this.props.initialValues.enableExternalServicesCheckbox;
    }
    return dirty;
  }

  render() {
    return (
      <section>
        <div
          className={this.props.values.showAnalyticsSiteSetting ? AnalyticsExtensionsSettingsStyles.enableForSite : AnalyticsExtensionsSettingsStyles.Hide}>
          <CheckBoxWithLabelWidget
            checked={this.props.values.enableExternalServicesCheckbox}
            handleChange={(isBoxChecked: boolean) => {
              this.handleChangeAdapter(isBoxChecked, 'enableExternalServicesCheckbox');
            }}
            testId="anext_site_enable_checkbox"
            label={Messages.analyticsExtensionsSettingsSite_checkbox()}
            disabled={!this.props.values.showSiteCheckbox}/>
        </div>
        <div>
          <div className={(this.props.values.showAnalyticsSiteSetting && !FeatureFlags.anExtRedesignV20203Enabled) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsRow}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <SingleSelectDropdown
                  testId="anext_site_connection_dropdown"
                  label={Messages.analyticsExtensionsSettingsConnectoinType()}
                  list={<List items={connectionItems} selectedValue={this.props.values.connectionType}/>}
                  onChange={(connectionType: string) => {
                    this.handleChangeAdapter(connectionType, 'connectionType');
                  }
                  }
                  onBlur={e => {
                    this.props.handleBlur(e);
                  }}
                  style={{width: 145}}
                  type={DropdownButtonType.Line}
                  disabled={false}
                  value={this.props.values.connectionType}
                  valid={this.props.errors.connectionType ? false : undefined}
                />
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <TextFieldWidget
                  testId="anext_site_host_textfield"
                  label={Messages.host()}
                  onChange={e => {
                    this.handleChangeAdapter(e.target.value, 'hostName');
                    this.clearLoginInfo();
                  }
                  }
                  onBlur={e => {
                    this.props.handleBlur(e);
                  }}
                  value={this.props.values.hostName}
                  disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
                  valid={this.props.errors.hostName ? false : undefined}
                />
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <TextFieldWidget
                  testId="anext_site_port_textfield"
                  label={Messages.port()}
                  onChange={e => {
                    this.handleChangeAdapter(e.target.value, 'port');
                    this.clearLoginInfo();}
                  }
                  onBlur={e => {this.props.handleBlur(e);}}
                  value={this.props.values.port ?? ''}
                  className={AnalyticsExtensionsSettingsStyles.connectionPortInput}
                  disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
                  valid={this.props.errors.port? false : undefined}
                />
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <CheckBoxWithLabelWidget
                  checked={this.props.values.isSecure}
                  handleChange={(isBoxChecked: boolean) => {
                    this.handleChangeAdapter(isBoxChecked, 'isSecure');
                    this.clearLoginInfo();
                  }}
                  testId="anext_site_secure_checkbox"
                  label={Messages.ssl()}
                  containerStyle={AnalyticsExtensionsSettingsStyles.connectionSslCheckbox}
                  disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
                />
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <CheckBoxWithLabelWidget
                  checked={this.props.values.isAuth}
                  handleChange={(isBoxChecked: boolean) => {
                    this.handleChangeAdapter(isBoxChecked, 'isAuth');
                    this.clearLoginInfo();
                  }}
                  testId="anext_site_auth_checkbox"
                  label={Messages.authentication()}
                  disabled={this.props.values.connectionType === ConnectionTypes.SELECT}
                />
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <TextFieldWidget
                  testId="anext_site_userName_textfield"
                  label={Messages.username()}
                  onChange={e => {
                    this.handleChangeAdapter(e.target.value, 'userName');
                  }}
                  onBlur={e => {
                    this.props.handleBlur(e);
                  }}
                  value={this.props.values.userName}
                  disabled={(this.props.values.connectionType === ConnectionTypes.SELECT) || (!this.props.values.isAuth)}
                  valid={this.props.errors.userName ? false : undefined}
                />
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionSettingsCell}>
                <TextFieldWidget
                  testId="anext_site_password_textfield"
                  label={Messages.password()}
                  onChange={e => {
                    this.handleChangeAdapter(e.target.value, 'password');
                  }}
                  onBlur={e => {
                    this.props.handleBlur(e);
                  }}
                  value={this.props.values.password}
                  disabled={(this.props.values.connectionType === ConnectionTypes.SELECT) || (!this.props.values.isAuth)}
                  type={'password'}
                  valid={this.props.errors.password ? false : undefined}
                />
              </div>
            </div>
          </div>
          <div
            className={(this.props.values.showAnalyticsSiteSetting && FeatureFlags.anExtRedesignV20203Enabled) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
            <div className={AnalyticsExtensionsSettingsStyles.connectionRow}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCell} style={{width: '40%'}}>
                {Messages.analyticsExtensionsSettingsConnectionType()}
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionHeaderCell} style={{width: '60%'}}>
                {Messages.analyticsExtensionsSettingsHostname()}
              </div>
            </div>
          </div>
          <div className={(this.props.values.showAnalyticsSiteSetting && FeatureFlags.anExtRedesignV20203Enabled) ? AnalyticsExtensionsSettingsStyles.connectionSettingsTable : AnalyticsExtensionsSettingsStyles.Hide}>
            <div
              className={this.props.values.connectionType === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.connectionRow : AnalyticsExtensionsSettingsStyles.Hide}>
              <div className={AnalyticsExtensionsSettingsStyles.emptyConnectionPrompt}>
                {Messages.analyticsExtensionsNoConnectionConfigured()}
                <TextLinkWidget testId="anext_connection_create_textlink" onClick={() => {
                  this.showConnectionDialog(this.props).then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                }}>
                  <u style={{fontStyle: 'normal', fontWeight: 'normal', paddingLeft: '5px', paddingRight: '5px'}}>
                    {Messages.analyticsExtensionsSettingsCreateConnection()}
                  </u>
                </TextLinkWidget>
              </div>
            </div>
            <div
              className={this.props.values.connectionType === ConnectionTypes.SELECT ? AnalyticsExtensionsSettingsStyles.Hide : AnalyticsExtensionsSettingsStyles.connectionRow}>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '40%'}}>
                {this.props.values.connectionType}
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '40%'}}>
                {this.props.values.hostName}
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '5%'}}>
                <TextLinkWidget testId="anext_connection_edit_textlink" onClick={() => {
                  this.showConnectionDialog(this.props).then(
                    (result: IAnalyticsExtConnectionSettings) => {
                      this.updateConnectionInfo(result);
                    }
                  );
                }}>
                  <u style={{fontWeight: 'normal'}}>
                    {Messages.analyticsExtensionsSettingsEdit()}
                  </u>
                </TextLinkWidget>
              </div>
              <div className={AnalyticsExtensionsSettingsStyles.connectionCell} style={{width: '5%'}}>
                <TextLinkWidget testId="anext_connection_delete_textlink" onClick={this.sendDelete}>
                  <u style={{fontWeight: 'normal'}}>
                    {Messages.analyticsExtensionsSettingsDelete()}
                  </u>
                </TextLinkWidget>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export let defaultConnection: IAnalyticsExtConnectionSettings = {
  connection_luid: '',
  connection_type: 'SELECT',
  host: '',
  port: undefined,
  is_ssl_enabled: false,
  is_auth_enabled: false,
  username: '',
  password: ''
};

export const AnalyticsExtensionsSettingsForSite =
  withFormik < AnalyticsExtensionsSettingsCtrlProps, AnalyticsExtensionsSettingsForSiteValues>({
    enableReinitialize: true,   // Reset the form when initial props change
    validateOnBlur: true,
    mapPropsToValues: props => {
      let initInfoSite = false;
      let initInfoConnection =  defaultConnection;
      let connection = ConnectionTypes.SELECT;
      if (typeof props.initialAnalyticsExtensionsSettings !== 'undefined'){
        initInfoSite = props.initialAnalyticsExtensionsSettings.enableExtSvcSite;
      }
      if (props.initialAnalyticsExtensionsSettings?.connectionDefault){
        initInfoConnection = props.initialAnalyticsExtensionsSettings.connectionDefault;
      }
      if (initInfoConnection.connection_type ===  ConnectionTypes.TABPY.TAG){
        connection = ConnectionTypes.TABPY.DISPLAY;
      }
      if (initInfoConnection.connection_type === ConnectionTypes.RSERVE.TAG){
        connection = ConnectionTypes.RSERVE.DISPLAY;
      }

      return {
        showAnalyticsSiteSetting:props.displaySettings,
        showSiteCheckbox:props.displaySiteSettingCheckbox,
        enableExternalServicesCheckbox: initInfoSite,
        connectionLUID: initInfoConnection.connection_luid,
        connectionName: 'Default',
        connectionType: connection,
        hostName: initInfoConnection.host,
        port: initInfoConnection.port,
        isSecure: initInfoConnection.is_ssl_enabled,
        isAuth: initInfoConnection.is_auth_enabled,
        userName: initInfoConnection.username,
        password: initInfoConnection.password
      };
    },
    validate: (values, props) => {
      const errors: any = {};
      const invalid = 'Invalid';
      if(!FeatureFlags.anExtRedesignV20203Enabled) {
        let connectionTypeSelected = (values.connectionType) && (values.connectionType !== ConnectionTypes.SELECT);
        // If there wasn't a connection previously, and no connection created just now, there is no validation error
        if ((!connectionTypeSelected) && (!props.initialAnalyticsExtensionsSettings?.connectionDefault)) {
          return errors;
        } else {
          if ((!connectionTypeSelected) && (props.initialAnalyticsExtensionsSettings?.connectionDefault)) {
            errors.connectionType = invalid;
          }
          if ((!values.hostName) || (values.hostName.length > MAX_HOSTNAME_LENGTH)) {
            errors.hostName = invalid;
          }
          if ((!values.port) || (!Number.isInteger(+values.port)) || (+values.port < MIN_PORT_NUMBER) || (+values.port > MAX_PORT_NUMBER)) {
            errors.port = invalid;
          }
          if (values.isAuth && values.userName && (values.userName.length > MAX_USERNAME_LENGTH)) {
            errors.userName = invalid;
          }
          if (values.isAuth && values.password && (values.password.length > MAX_PASSWORD_LENGTH)) {
            errors.password = invalid;
          }
        }
      }
      return errors;
    },
    handleSubmit: (values, actions) => {
      // Reset the form after submission happens
      actions.setSubmitting(false);
      actions.resetForm({values});
    },
   })(InnerAnalyticsExtensionsSettingForSite);
